import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerkAuth } from './use-clerk-auth';
import config from '../config';
import apiClient from '../services/api-client';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI;

const SCOPES = ["https://graph.microsoft.com/Calendars.ReadWrite", "offline_access"]
const OUTLOOK_REDIRECT_URI = process.env.REACT_APP_OUTLOOK_REDIRECT_URI;
const OUTLOOK_CLIENT_ID = process.env.REACT_APP_OUTLOOK_CLIENT_ID;

export const useCalendarActions = () => {
  const [calendarType, setCalendarType] = useState(null);
  const navigate = useNavigate();
  const { user, userId, getSessionToken } = useClerkAuth();
  
  const checkExistingCredentials = async (calendarType) => {
    try {
      const response = await apiClient.get(
        `/api/check-calendar-credentials`,
        { calendar_type: calendarType },
        getSessionToken
      );
      // The response is wrapped in a data field by success_response
      return response?.data?.has_credentials || false;
    } catch (error) {
      console.error('Error checking credentials:', error);
      return false;
    }
  };

  const handleGoogleLogin = () => {
    localStorage.setItem("authInProgress", "true"); // dont need this

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${GOOGLE_CLIENT_ID}` +
        `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
        `&response_type=code` +  
        `&scope=${encodeURIComponent("https://www.googleapis.com/auth/calendar.events")}` +
        `&access_type=offline` +  
        `&prompt=select_account` +
        `&state=${encodeURIComponent(userId || '')}`;

    window.location.href = authUrl;
  };

  const handleOutlookLogin = () => {
    localStorage.setItem("authInProgress", "true");
    
    // Build Outlook OAuth URL (moved from backend)
    const outlookAuthUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${OUTLOOK_CLIENT_ID}` +
        `&response_type=code` +
        `&redirect_uri=${encodeURIComponent(OUTLOOK_REDIRECT_URI)}` +
        `&response_mode=query` +
        `&scope=${encodeURIComponent(SCOPES.join(' '))}` +
        `&state=${encodeURIComponent(userId || '')}` +
        `&prompt=select_account`;

    window.location.href = outlookAuthUrl;
  };

  const handleSelectCalendar = async (calendar) => {
    setCalendarType(calendar);
    localStorage.setItem("calendarType", calendar);

    if (calendar === "google") {
        // Check if we already have credentials
        const hasCredentials = await checkExistingCredentials("google");
        if (hasCredentials) {
            console.log("Google credentials already exist, skipping OAuth");
            navigate(`/add-events/google`);
        } else {
            console.log("No Google credentials found, initiating OAuth");
            handleGoogleLogin();
        }
    } else if (calendar === "outlook") {
        // Check if we already have credentials
        const hasCredentials = await checkExistingCredentials("outlook");
        if (hasCredentials) {
            console.log("Outlook credentials already exist, skipping OAuth");
            navigate(`/add-events/outlook`);
        } else {
            console.log("No Outlook credentials found, initiating OAuth");
            handleOutlookLogin();
        }
    } else if (calendar === "apple") {
        navigate(`/add-events/${calendar}`);
    } else if (calendar === "ocean") {
        // Ocean calendar - no OAuth needed, go directly to add-events
        navigate(`/add-events/ocean`);
    } else {
        alert("Invalid calendar type");
    }
  };

  const handleAddEventsToCalendar = async () => {
    if ((calendarType === "google" || calendarType === "outlook") && !user) {
        alert("You must log in before adding events.");
        return;
    }
      

    if (!calendarType) {
        alert("No calendar selected.");
        return;
    }
    
    try {
    const rawSelectedSections = localStorage.getItem("selectedSections");
    const rawCalendarType = localStorage.getItem("calendarType");

    if (!rawSelectedSections || !rawCalendarType) {
        alert("Missing sections or calendar type");
        return;
    }

    // Parse outer object
    const parsedRaw = JSON.parse(rawSelectedSections);

    // Manually parse each section object if needed
    const selectedSections = {};
    for (const [key, value] of Object.entries(parsedRaw)) {
        selectedSections[key] =
        typeof value === "string" ? JSON.parse(value) : value;
    }
    console.log("Selected sections:", selectedSections);
    const data = await apiClient.post('/select_section', {
        selected_sections: selectedSections,
        calendar_type: rawCalendarType,
    }, () => Promise.resolve(''));
    
    if (data.status === 'success') {
        if (rawCalendarType === "apple") {
            const blob = await apiClient.download('/apple/download_generated_ics', {}, () => Promise.resolve(''));
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "ocean_schedule.ics");
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
        else if (rawCalendarType === "google" || rawCalendarType === "outlook") {
            alert("Events successfully added to your calendar!");
        }
    } else {
        alert(`Error adding events: ${data.error}`);
      }
    } catch (error) {
      console.error("Error sending data:", error);
      alert("Something went wrong!");
    }
  };

  useEffect(() => {
    const authInProgress = localStorage.getItem("authInProgress");
    const storedCalendarType = localStorage.getItem("calendarType");

    if (authInProgress) {
        localStorage.removeItem("authInProgress");

        if (storedCalendarType) {
            setCalendarType(storedCalendarType);
        }
    }
  }, [navigate]);

  return {
    calendarType,
    selectCalendar: handleSelectCalendar,
    addEventsToCalendar: handleAddEventsToCalendar
  }
}
